import React, { useEffect, useState } from "react";
import api, { inr, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Download, FileSpreadsheet, AlertTriangle } from "lucide-react";

function expiryStatus(dateStr) {
  if (!dateStr) return null;
  const exp = new Date(dateStr);
  if (isNaN(exp)) return null;
  const months = (exp - new Date()) / (1000 * 60 * 60 * 24 * 30.4);
  if (months < 0) return { label: "EXPIRED", cls: "border-red-500/60 text-red-300 bg-red-500/10" };
  if (months <= 6) return { label: "Expiring soon", cls: "border-amber-400/60 text-amber-300 bg-amber-500/10" };
  return null;
}

export default function Passengers() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/passengers/summary").then((r) => setSummary(r.data));
    api.get("/passengers?package_id=all").then((r) => setRows(r.data));
  }, []);

  const exportExcel = (pkgId = "all") => {
    const token = localStorage.getItem("token");
    const ws = localStorage.getItem("workspace_id");
    window.open(`${API}/passengers/export?workspace_id=${ws}&package_id=${encodeURIComponent(pkgId)}&auth=${token}`, "_blank");
  };

  const exportDocsZip = (pkgId = "all") => {
    const token = localStorage.getItem("token");
    const ws = localStorage.getItem("workspace_id");
    window.open(`${API}/documents/export-package?workspace_id=${ws}&package_id=${encodeURIComponent(pkgId)}&auth=${token}`, "_blank");
  };

  return (
    <div>
      <PageHeader eyebrow="Group Manager" title="Passengers & Group Lists"
        subtitle="Auto-compiled passenger lists per package with one-click Excel export"
        action={
          <div className="flex gap-2">
            <Button data-testid="export-all-docs-btn" onClick={() => exportDocsZip("all")} variant="outline" className="border-gold/40 text-gold hover:bg-gold/15 font-semibold gap-2">
              <Download className="w-4 h-4" /> All Docs (ZIP)
            </Button>
            <Button data-testid="export-all-excel-btn" onClick={() => exportExcel("all")} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Export All (Excel)
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-8 space-y-5">
        {(() => {
          const expiring = rows.filter((p) => expiryStatus(p.passport_expiry));
          return expiring.length > 0 ? (
            <Card data-testid="expiry-alert-banner" className="p-4 bg-amber-500/10 border-amber-400/40 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0" />
              <p className="text-sm text-amber-200"><strong>{expiring.length}</strong> passenger{expiring.length > 1 ? "s have" : " has"} a passport expiring within 6 months (or already expired). Check the highlighted rows below.</p>
            </Card>
          ) : null;
        })()}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 bg-card/40 border-gold/30 gold-glow">
            <div className="flex items-center gap-2 text-gold mb-2"><Users className="w-4 h-4" /><span className="eyebrow text-muted-foreground">Total Passengers</span></div>
            <p className="text-3xl font-serif font-semibold text-white">{summary?.total_passengers ?? 0}</p>
          </Card>
          {(summary?.groups || []).slice(0, 3).map((g) => (
            <Card key={g.package_name} className="p-4 bg-card/40 border-border/60">
              <p className="eyebrow text-gold truncate">{g.package_name}</p>
              <p className="text-2xl font-serif font-semibold text-white mt-1">{g.passengers} pax</p>
              <p className="text-[11px] text-muted-foreground">{g.male}M · {g.female}F · {g.families} families</p>
            </Card>
          ))}
        </div>

        {/* Group summary with per-package export */}
        <Card className="p-5 bg-card/40 border-border/60">
          <p className="eyebrow text-gold mb-3">Group Summary by Package</p>
          <div className="space-y-2">
            {(summary?.groups || []).length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No passengers added yet. Customers add them from the portal.</p>}
            {(summary?.groups || []).map((g) => (
              <div key={g.package_name} className="flex items-center gap-3 p-3 rounded-lg bg-card/50 border border-border/50">
                <div className="flex-1">
                  <p className="text-sm font-medium">{g.package_name}</p>
                  <p className="text-[11px] text-muted-foreground">{g.passengers} passengers · {g.families} families · {g.male} male / {g.female} female · Revenue {inr(g.revenue)}</p>
                </div>
                <Badge variant="outline" className="border-gold/40 text-gold">{g.passengers} pax</Badge>
                <Button size="sm" variant="outline" data-testid={`docs-pkg-${g.package_name}`} onClick={() => exportDocsZip(rows.find((r) => r.package_name === g.package_name)?.package_id || "all")} className="border-gold/40 text-gold gap-1.5 text-xs">
                  <Download className="w-3.5 h-3.5" /> Docs
                </Button>
                <Button size="sm" variant="outline" data-testid={`export-pkg-${g.package_name}`} onClick={() => exportExcel(rows.find((r) => r.package_name === g.package_name)?.package_id || "all")} className="border-gold/40 text-gold gap-1.5 text-xs">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                </Button>
              </div>
            ))}
          </div>
        </Card>

        {/* Full passenger table */}
        <Card className="p-5 bg-card/40 border-border/60 overflow-auto">
          <p className="eyebrow text-gold mb-3">All Passengers</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border/60">
                <th className="py-2 pr-3">Name</th><th className="pr-3">Surname</th><th className="pr-3">Passport</th>
                <th className="pr-3">Expiry</th><th className="pr-3">DOB</th><th className="pr-3">Gender</th><th className="pr-3">Package</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const st = expiryStatus(p.passport_expiry);
                return (
                <tr key={p.id} data-testid={`pax-row-${p.id}`} className={`border-b border-border/40 ${st ? "bg-amber-500/5" : ""}`}>
                  <td className="py-2 pr-3">{p.first_name}</td><td className="pr-3">{p.last_name}</td>
                  <td className="pr-3 font-mono text-xs">{p.passport_no || "—"}</td>
                  <td className="pr-3">
                    <span className="flex items-center gap-1.5">{p.passport_expiry || "—"}
                      {st && <Badge variant="outline" className={`text-[9px] ${st.cls}`}>{st.label}</Badge>}
                    </span>
                  </td>
                  <td className="pr-3">{p.dob || "—"}</td><td className="pr-3 capitalize">{p.gender || "—"}</td>
                  <td className="pr-3 text-xs text-gold">{p.package_name || "—"}</td>
                </tr>
              );})}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center text-xs text-muted-foreground py-4">No passengers yet.</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
